import { useCallback, useState } from 'react'
import type { LoadHubLoadboardStatusEntry } from '@numeo/types'
import { loadsIngestServiceAxios } from 'setup/SetupAxios'

export type AihubSource =
    | 'truckstop-account'
    | 'beon'
    | 'navisphere'
    | 'arrive'
    | 'tql'
    | 'spot'
    | 'emerge'
    | 'echo'
    | 'mode-global'
    | 'knx'
    | 'rxo'
    | 'convoy'
    | 'jb-hunt'
    | 'schneider'
    | 'uber-freight'
    | 'dat'

export type AihubBoardStatus = LoadHubLoadboardStatusEntry

interface UseAihubTokenStatusReturn {
    checkStatus: (source: AihubSource) => Promise<AihubBoardStatus | null>
    checkAllStatuses: (sources?: AihubSource[]) => Promise<Record<string, AihubBoardStatus>>
    refreshNavisphereToken: () => Promise<{ success: boolean; error?: string }>
    refreshing: boolean
}

async function fetchStatus(source: AihubSource): Promise<AihubBoardStatus | null> {
    const response = await loadsIngestServiceAxios.get('/loads/loadboards/status', { params: { source, _t: Date.now() } })
    return response.data?.data ?? null
}

async function fetchBulkStatuses(sources?: AihubSource[]): Promise<Record<string, AihubBoardStatus>> {
    const params: Record<string, string | number> = { _t: Date.now() }
    if (sources?.length) params.sources = sources.join(',')
    const response = await loadsIngestServiceAxios.get('/loads/loadboards/status/bulk', { params })
    return response.data?.data ?? {}
}

async function serverRefreshNavisphere(): Promise<{ success: boolean; error?: string }> {
    try {
        await loadsIngestServiceAxios.post('/loads/loadboards/navisphere/refresh')
        return { success: true }
    } catch (err: unknown) {
        const message = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message || (err as Error).message
        return { success: false, error: message }
    }
}

export function useAihubTokenStatus(): UseAihubTokenStatusReturn {
    const [refreshing, setRefreshing] = useState(false)

    const checkStatus = useCallback(async (source: AihubSource): Promise<AihubBoardStatus | null> => {
        return fetchStatus(source)
    }, [])

    const checkAllStatuses = useCallback(async (sources?: AihubSource[]): Promise<Record<string, AihubBoardStatus>> => {
        return fetchBulkStatuses(sources)
    }, [])

    const refreshNavisphereToken = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
        setRefreshing(true)
        try {
            return await serverRefreshNavisphere()
        } finally {
            setRefreshing(false)
        }
    }, [])

    return { checkStatus, checkAllStatuses, refreshNavisphereToken, refreshing }
}
